import { useEffect, useMemo, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import PageMeta from "@/components/PageMeta";
import { slugify } from "@/lib/slug";
import { buildHistoricalMetaDescription } from "@/lib/seoText";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { useSiteFlags } from "@/contexts/SiteFlagsContext";
import { useVoiceSearch } from "@/contexts/VoiceSearchContext";
import { CheckCircle, Scroll, ArrowLeft, Trophy, GlobeHemisphereWest } from "@phosphor-icons/react";
import HeartButton from "@/components/HeartButton";

export default function HistoricalRecipe() {
  const { slug } = useParams();
  const { user } = useAuth();
  const { flags, loading: flagsLoading } = useSiteFlags();
  const nav = useNavigate();
  const [r, setR] = useState(null);
  // Expose this recipe to voice ("add this to my meal plan", "add to shopping list").
  const { setVoiceRecipe } = useVoiceSearch();
  const voiceRecipe = useMemo(() => r && {
    recipe_id: r.historical_id, title: r.title, is_historical: true,
    ingredients: (r.ingredients || []).map((name) => ({ name })), instructions: r.instructions || [],
  }, [r]);
  useEffect(() => { setVoiceRecipe(voiceRecipe); return () => setVoiceRecipe(null); }, [voiceRecipe, setVoiceRecipe]);
  const [loading, setLoading] = useState(true);
  const [cooked, setCooked] = useState(false);
  const [busy, setBusy] = useState(false);
  const [related, setRelated] = useState({ century: [], author: [] });

  useEffect(() => {
    if (flagsLoading) return;
    if (!flags.historical_recipes_enabled) { nav("/", { replace: true }); return; }
    (async () => {
      try {
        const { data } = await api.get(`/historical/recipes/${slug}`);
        setR(data);
        // Canonicalise the URL: if the user landed via legacy historical_id
        // OR an outdated slug, replace to the current slug for SEO. Prevents
        // duplicate-URL confusion in Google Search Console.
        const canonicalSlug = data.slug || data.historical_id;
        if (canonicalSlug && canonicalSlug !== slug) {
          nav(`/historical/${canonicalSlug}`, { replace: true });
        }
        if (user) {
          const p = await api.get("/historical/progress");
          setCooked((p.data.recipes_cooked || []).includes(data.historical_id));
        }
        // Fire-and-forget: pull same-century + same-author lists for the
        // "Related recipes" internal-link block. This creates SEO link-juice
        // loops between the 50 historical hero pages so Google crawls the
        // section faster and gives them more topical authority.
        (async () => {
          try {
            const [byCentury, byAuthor] = await Promise.all([
              api.get("/historical/recipes", { params: { century: data.century, limit: 12 } }),
              api.get("/historical/recipes", { params: { q: data.author, limit: 12 } }),
            ]);
            const excludeSelf = (arr) => (arr || []).filter((x) => x.historical_id !== data.historical_id);
            const sameAuthor = excludeSelf(byAuthor?.data?.items).filter((x) => x.author === data.author).slice(0, 4);
            const sameCentury = excludeSelf(byCentury?.data?.items).filter((x) => x.author !== data.author || sameAuthor.length === 0).slice(0, 6);
            setRelated({ century: sameCentury, author: sameAuthor });
          } catch { /* silent — not critical */ }
        })();
      } catch (e) {
        toast.error("Recipe not found");
      } finally { setLoading(false); }
    })();
  }, [slug, user, flags.historical_recipes_enabled, nav, flagsLoading]);

  const markCooked = async () => {
    if (!user) { nav(`/login?next=/historical/${slug}`); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/historical/recipes/${r.historical_id}/mark-cooked`);
      setCooked(true);
      const newBadges = data.newly_earned_badges || [];
      if (newBadges.length > 0) {
        toast.success(`🏆 Badge unlocked: ${newBadges.join(", ")}`, { duration: 6000 });
      } else if (!data.already_cooked) {
        toast.success("Progress logged. Enjoy your time-travelling meal!");
      } else {
        toast.info("Already logged for this recipe.");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't log progress");
    } finally { setBusy(false); }
  };

  if (loading) return <div className="p-16 text-center text-rf-ink-soft">Loading…</div>;
  if (!r) return null;

  // Canonical URL uses the slug — same rule the redirect above enforces.
  const canonicalSlug = r.slug || r.historical_id;
  const canonicalUrl = `https://recipefiler.com/historical/${canonicalSlug}`;

  // JSON-LD Recipe schema for Google Rich Results — client-side ins for
  // JS-enabled crawlers. Bot User-Agents get the full SSR HTML via
  // /api/render/historical/{slug} routed by the Cloudflare Worker.
  const jsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "Recipe",
      name: r.title,
      description: buildHistoricalMetaDescription(r),
      image: r.image_url || "https://recipefiler.com/recipe-default.jpg",
      author: { "@type": "Person", name: r.author || "Historical" },
      datePublished: r.created_at,
      recipeCuisine: r.country,
      recipeCategory: r.era_label || undefined,
      keywords: (r.tags || []).join(", ") || undefined,
      recipeIngredient: r.ingredients || [],
      recipeInstructions: (r.instructions || []).map((step, i) => ({
        "@type": "HowToStep",
        position: i + 1,
        text: step,
        image: r.image_url || "https://recipefiler.com/recipe-default.jpg",
      })),
      recipeYield: r.serves ? String(r.serves) : undefined,
      totalTime: r.prep_minutes || r.cook_minutes ? `PT${(r.prep_minutes || 0) + (r.cook_minutes || 0)}M` : undefined,
      mainEntityOfPage: canonicalUrl,
      url: canonicalUrl,
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: "https://recipefiler.com/" },
        { "@type": "ListItem", position: 2, name: "Historical Recipes", item: "https://recipefiler.com/historical" },
        { "@type": "ListItem", position: 3, name: r.title },
      ],
    },
  ];

  return (
    <div className="min-h-screen bg-rf-cream" data-testid="historical-recipe-page">
      <PageMeta
        title={r.seo_title || `${r.title} — ${r.source_book} (${r.year > 0 ? r.year : `${Math.abs(r.year)} BC`})`}
        description={buildHistoricalMetaDescription(r)}
        canonical={canonicalUrl}
        ogImage={r.image_url || "/recipe-default.jpg"}
        jsonLd={jsonLd}
      />
      <div className="max-w-4xl mx-auto px-6 py-8">
        <Link to="/historical" className="inline-flex items-center gap-2 text-sm text-rf-ink-soft hover:text-rf-ink mb-6">
          <ArrowLeft size={16} weight="bold" /> Back to Historical Recipes
        </Link>

        <div className="bg-white rounded-3xl overflow-hidden border border-rf-ink/5 shadow-sm">
          {/* Hero image (when set) */}
          {r.image_url && (
            <img
              src={r.image_url}
              alt={r.image_alt || `${r.title} — ${r.era_label || ""} historical recipe`}
              className="w-full aspect-[16/9] object-cover"
              data-testid="historical-hero-image"
              onError={(e) => { e.currentTarget.style.display = "none"; }}
            />
          )}
          {/* Header */}
          <div className="bg-gradient-to-br from-amber-800 to-rf-ink text-rf-cream p-8 relative">
            <div className="absolute inset-0 opacity-5 flex items-center justify-center">
              <Scroll size={200} weight="fill" />
            </div>
            <div className="relative">
              <p className="overline !text-rf-amber mb-2">
                {r.era_label || `${r.century}th Century`} ·{" "}
                <Link
                  to={`/historical/region/${slugify(r.country || "")}`}
                  className="hover:underline"
                  data-testid="historical-region-link"
                >
                  {r.country}
                </Link>
              </p>
              <h1 className="font-display font-black text-3xl sm:text-5xl leading-tight mb-3">{r.title}</h1>
              <h2 className="text-sm italic opacity-90 font-normal">
                From <b className="not-italic">{r.source_book}</b> · {r.author} · {r.year > 0 ? r.year : `${Math.abs(r.year)} BC`}
              </h2>
              {r.source_verified && (
                <p className="text-xs mt-3 inline-flex items-center gap-1 bg-emerald-500/20 text-emerald-100 px-2 py-1 rounded-full">
                  <CheckCircle size={12} weight="fill" /> Verified from original source
                </p>
              )}
              <div className="mt-4">
                <HeartButton kind="historical" id={r.historical_id} count={r.favourite_count} variant="pill" />
              </div>
            </div>
          </div>

          <div className="p-8">
            {r.description && <p className="text-lg text-rf-ink leading-relaxed mb-6">{r.description}</p>}
            {r.original_note && (
              <div className="bg-rf-cream-alt rounded-2xl p-4 mb-6 border-l-4 border-rf-orange italic text-sm text-rf-ink-soft">
                <b className="text-rf-ink not-italic">Historical note:</b> {r.original_note}
              </div>
            )}

            {/* History summary — only render when admin has flipped
                history_verified. Preserves the "100% correct" bar. */}
            {r.history_verified && r.history_summary && (
              <section className="bg-rf-cream-alt rounded-2xl p-5 mb-8" data-testid="historical-history-section">
                <h2 className="font-display font-black text-xl text-rf-ink mb-3">History</h2>
                <p className="text-rf-ink leading-relaxed">{r.history_summary}</p>
                {(r.history_sources || []).length > 0 && (
                  <>
                    <h3 className="overline mt-4 mb-2">Sources</h3>
                    <ul className="text-xs text-rf-ink-soft space-y-1 list-disc pl-5">
                      {(r.history_sources || []).map((s, i) => (
                        <li key={i}>{s}</li>
                      ))}
                    </ul>
                  </>
                )}
              </section>
            )}

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-8 text-center">
              {r.serves && <div><p className="text-xs text-rf-ink-soft uppercase tracking-widest">Serves</p><p className="font-display font-black text-2xl text-rf-ink">{r.serves}</p></div>}
              {r.prep_minutes && <div><p className="text-xs text-rf-ink-soft uppercase tracking-widest">Prep</p><p className="font-display font-black text-2xl text-rf-ink">{r.prep_minutes}<span className="text-sm">m</span></p></div>}
              {r.cook_minutes !== undefined && r.cook_minutes !== null && <div><p className="text-xs text-rf-ink-soft uppercase tracking-widest">Cook</p><p className="font-display font-black text-2xl text-rf-ink">{r.cook_minutes}<span className="text-sm">m</span></p></div>}
              {r.difficulty && <div><p className="text-xs text-rf-ink-soft uppercase tracking-widest">Difficulty</p><p className="font-display font-black text-2xl text-rf-ink">{r.difficulty}</p></div>}
            </div>

            <div className="grid md:grid-cols-2 gap-8 mb-8">
              <div>
                <h2 className="font-display font-black text-xl text-rf-ink mb-3">Ingredients</h2>
                <ul className="space-y-2" data-testid="historical-ingredients">
                  {(r.ingredients || []).map((ing, i) => (
                    <li key={i} className="text-rf-ink flex gap-2">
                      <span className="text-rf-orange font-bold">·</span> {ing}
                    </li>
                  ))}
                </ul>
              </div>
              <div>
                <h2 className="font-display font-black text-xl text-rf-ink mb-3">Method</h2>
                <ol className="space-y-3" data-testid="historical-instructions">
                  {(r.instructions || []).map((step, i) => (
                    <li key={i} className="flex gap-3">
                      <span className="shrink-0 w-7 h-7 rounded-full bg-rf-orange text-white font-black flex items-center justify-center text-sm">{i + 1}</span>
                      <p className="text-rf-ink pt-0.5">{step}</p>
                    </li>
                  ))}
                </ol>
              </div>
            </div>

            <button
              type="button"
              data-testid="historical-mark-cooked"
              onClick={markCooked}
              disabled={busy || cooked}
              className={`btn-amber w-full inline-flex items-center justify-center gap-2 ${cooked ? "!bg-emerald-600" : ""}`}
            >
              {cooked ? (<><CheckCircle size={18} weight="fill" /> Already cooked — logged to your journey</>)
                : busy ? "Logging…"
                : (<><Trophy size={18} weight="bold" /> I cooked this — log to my journey</>)}
            </button>
          </div>
        </div>

        {/* Related historical recipes — internal-link block creating SEO
            link-juice loops between the 50 historical hero pages. Google
            crawls related URLs faster once one recipe is indexed. Only
            renders when we actually found related content. */}
        {(related.author.length > 0 || related.century.length > 0) && (
          <section className="mt-12" data-testid="historical-related-section">
            {related.author.length > 0 && (
              <div className="mb-8">
                <h2 className="font-display font-black text-xl sm:text-2xl text-rf-ink mb-4">
                  More by {r.author}
                </h2>
                <ul className="grid grid-cols-2 sm:grid-cols-4 gap-4" data-testid="historical-related-author">
                  {related.author.map((x) => (
                    <li key={x.historical_id}>
                      <Link
                        to={`/historical/${x.slug || x.historical_id}`}
                        className="block group"
                        data-testid={`historical-related-author-${x.historical_id}`}
                      >
                        <div className="aspect-[4/3] rounded-lg overflow-hidden bg-gradient-to-br from-amber-800 to-rf-ink relative shadow-sm group-hover:shadow-lg transition-shadow">
                          {x.image_url ? (
                            <img src={x.image_url} alt={x.title} loading="lazy" className="w-full h-full object-cover" onError={(e) => { e.currentTarget.style.display = "none"; }} />
                          ) : (
                            <div className="absolute inset-0 flex items-center justify-center opacity-10">
                              <Scroll size={48} weight="fill" className="text-rf-cream" />
                            </div>
                          )}
                        </div>
                        <p className="font-display font-black text-sm text-rf-ink mt-2 line-clamp-2 group-hover:text-rf-orange leading-tight">{x.title}</p>
                        <p className="text-xs text-rf-ink-soft mt-1">{x.year > 0 ? x.year : `${Math.abs(x.year)} BC`}</p>
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {related.century.length > 0 && (
              <div>
                <h2 className="font-display font-black text-xl sm:text-2xl text-rf-ink mb-4">
                  Other {r.era_label || `${r.century > 0 ? `${r.century}th century` : `${Math.abs(r.century)}th century BC`}`} recipes
                </h2>
                <ul className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4" data-testid="historical-related-century">
                  {related.century.map((x) => (
                    <li key={x.historical_id}>
                      <Link
                        to={`/historical/${x.slug || x.historical_id}`}
                        className="block group"
                        data-testid={`historical-related-century-${x.historical_id}`}
                      >
                        <div className="aspect-[4/3] rounded-lg overflow-hidden bg-gradient-to-br from-amber-800 to-rf-ink relative shadow-sm group-hover:shadow-lg transition-shadow">
                          {x.image_url ? (
                            <img src={x.image_url} alt={x.title} loading="lazy" className="w-full h-full object-cover" onError={(e) => { e.currentTarget.style.display = "none"; }} />
                          ) : (
                            <div className="absolute inset-0 flex items-center justify-center opacity-10">
                              <Scroll size={40} weight="fill" className="text-rf-cream" />
                            </div>
                          )}
                        </div>
                        <p className="font-display font-black text-xs text-rf-ink mt-2 line-clamp-2 group-hover:text-rf-orange leading-tight">{x.title}</p>
                        <p className="text-[10px] text-rf-ink-soft mt-1">{x.author} · {x.year > 0 ? x.year : `${Math.abs(x.year)} BC`}</p>
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <div className="text-center mt-8">
              <Link
                to="/historical"
                className="inline-flex items-center gap-2 text-sm font-black text-rf-orange hover:text-rf-ink"
                data-testid="historical-related-back-to-all"
              >
                Browse all 50 historical recipes →
              </Link>
            </div>
          </section>
        )}
      </div>
    </div>
  );
}
